import { FC, MouseEvent, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as DateFilterIcon } from '@/assets/icons/date-filter.svg';
import { ReactComponent as FilterIcon } from '@/assets/icons/filter.svg';

import Button from '../Button';

interface BtnProps {
  $active: boolean;
  $withoutText: boolean;
}

const StyledButton = styled(Button)<BtnProps>`
  position: relative;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.black : theme.colors.gray};
  &:hover,
  &:active {
    color: ${({ theme }) => theme.colors.black};
    border-color: ${({ theme }) => theme.colors.grayscale};
  }
  ${({ $active }) =>
    $active &&
    css`
      &:hover,
      &:active {
        border-color: rgba(34, 34, 34, 0.25);
      }
    `}

  ${({ $withoutText }) =>
    $withoutText &&
    css`
      padding-left: 16px;
      padding-right: 16px;

      > svg {
        margin: 0;
      }
    `}
`;

interface ActiveDotIconProps {
  $type?: 'initial' | 'date';
}

export const ActiveDotIcon = styled.svg<ActiveDotIconProps>`
  width: 8px;
  height: 8px;
  position: absolute;
  left: 25px;
  top: 7px;
  fill: ${({ theme }) => theme.colors.error};
  stroke: ${({ theme }) => theme.colors.grayscaleLight};
  ${({ $type }) =>
    $type === 'date' &&
    css`
      left: 22px;
      top: 4px;
    `}
`;

interface Props {
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  isActive: boolean;
  filterApplied: boolean;
  className?: string;
  text?: ReactNode;
  type?: 'initial' | 'date';
  icon?: ReactNode;
  iconOnly?: boolean;
}

const FilterButton: FC<Props> = ({
  onClick,
  isActive,
  className,
  filterApplied,
  icon,
  text = 'Filter',
  type = 'initial',
  iconOnly = false,
}) => {
  const renderFilterIcon = () => {
    if (icon) {
      return icon;
    }

    switch (type) {
      case 'initial':
        return <FilterIcon />;
      case 'date':
        return <DateFilterIcon />;
    }
  };

  return (
    <StyledButton
      color="secondary"
      small
      onClick={onClick}
      className={className}
      $active={isActive || filterApplied}
      $withoutText={text === '' || iconOnly}
    >
      {renderFilterIcon()}
      {filterApplied && (
        <ActiveDotIcon
          $type={type}
          viewBox="0 0 8 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="4.18183" cy="3.81818" r="3.31818" />
        </ActiveDotIcon>
      )}
      {!iconOnly && text}
    </StyledButton>
  );
};

export default FilterButton;
